/*
 * Runs one user turn of the agent loop (notes/ai-assistant-phase1.md §3.2):
 * call the model, run the requested tools on the client, send the results
 * back, repeat until the model ends its turn.
 *
 * `messages` is the raw agent conversation (Messages API format) and is
 * updated in place. Assistant content is stored exactly as the server
 * returned it. The conversation always stays valid for the next request:
 * every tool_use gets a tool_result, including tools that were stopped,
 * declined or cut off. If the turn ends on a user message (tool results),
 * the next prompt is appended to that message.
 */

export const MAX_TURN_STEPS = 20;

export const NOT_RUN_TRUNCATED = "Not run: your output was cut off before this tool call was complete. Retry with a smaller call.";
export const NOT_RUN_STOPPED = "Not run: the user stopped the agent.";

function asBlocks(content) {
    return typeof content === "string" ? [{ type: "text", text: content }] : content;
}

/**
 * Add the prompt as a new user message, or to the trailing tool-result
 * message. Returns a rollback for when the first step produced no reply.
 */
export function appendPrompt(messages, text) {
    const last = messages[messages.length - 1];
    if (last?.role === "user") {
        const previous = last.content;
        last.content = [...asBlocks(previous), { type: "text", text }];
        return () => {
            last.content = previous;
        };
    }
    messages.push({ role: "user", content: [{ type: "text", text }] });
    return () => {
        messages.pop();
    };
}

function notRun(block, message) {
    return { type: "tool_result", tool_use_id: block.id, content: message, is_error: true };
}

function isAbort(error, signal) {
    const code = String(error?.code ?? "").replace(/^functions\//, "");
    return Boolean(signal?.aborted) || error?.name === "AbortError" || code === "cancelled";
}

/**
 * @param {object} params
 * @param {Array} params.messages    agent conversation, updated in place
 * @param {string} params.prompt
 * @param {(messages: Array, info: { step: number }) => Promise<{ content: Array, stopReason: string, userContent?: Array }>} params.callStep
 * @param {(block: object) => Promise<object>} params.runTool  returns a tool_result block
 * @param {AbortSignal} [params.signal]
 * @param {number} [params.maxSteps]
 * @returns {Promise<{ status: "done"|"stopped"|"error"|"refused"|"max_tokens"|"step_limit", steps: number, error?: unknown }>}
 */
export async function runAgentTurn({ messages, prompt, callStep, runTool, signal, maxSteps = MAX_TURN_STEPS }) {
    const rollback = appendPrompt(messages, prompt);
    let steps = 0;
    let replied = false;

    for (;;) {
        if (signal?.aborted) {
            if (!replied) rollback();
            return { status: "stopped", steps };
        }
        if (steps >= maxSteps) {
            return { status: "step_limit", steps };
        }
        steps++;

        let response;
        try {
            response = await callStep(messages, { step: steps });
        } catch (error) {
            if (!replied) rollback();
            return { status: isAbort(error, signal) ? "stopped" : "error", steps, error };
        }

        if (Array.isArray(response.userContent)) {
            messages[messages.length - 1].content = response.userContent;
        }
        if (response.stopReason === "refusal") {
            // Leave the refused step out of the conversation so the chat stays usable.
            if (!replied) rollback();
            return { status: "refused", steps };
        }

        replied = true;
        const content = Array.isArray(response.content) ? response.content : [];
        messages.push({ role: "assistant", content });

        const toolUses = content.filter((block) => block.type === "tool_use");
        if (toolUses.length === 0) {
            return { status: response.stopReason === "max_tokens" ? "max_tokens" : "done", steps };
        }
        if (response.stopReason === "max_tokens") {
            messages.push({ role: "user", content: toolUses.map((block) => notRun(block, NOT_RUN_TRUNCATED)) });
            return { status: "max_tokens", steps };
        }

        const results = [];
        for (const block of toolUses) {
            if (signal?.aborted) {
                results.push(notRun(block, NOT_RUN_STOPPED));
                continue;
            }
            try {
                results.push(await runTool(block));
            } catch (error) {
                results.push(notRun(block, `The tool failed: ${error?.message ?? error}`));
            }
        }
        messages.push({ role: "user", content: results });

        if (signal?.aborted) {
            return { status: "stopped", steps };
        }
        if (response.stopReason !== "tool_use") {
            return { status: "done", steps };
        }
    }
}
